/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { Button, ModalBody, ModalFooter, ModalHeader, TextArea, TextInput, Stack } from '@carbon/react';
import { useState } from 'react';
import { createPortal } from 'react-dom';

import * as Styled from './EditVersionModal.styled';

/**
 * EditVersionModal component to edit the version details.
 *
 * @param {Object} props - The component props.
 * @param {Function} [props.onClose] - Function to call when the modal is closed.
 * @param {Function} [props.onSubmit] - Function to call when the form is submitted.
 * @param {Boolean} props.isOpen - Boolean indicating if the modal is open.
 * @param {String | React.ReactNode} [props.nameLabel] - Label text for the name input field.
 * @param {Boolean} [props.isNameFieldEditable] - Boolean indicating if the name field is editable.
 * @param {String | React.ReactNode} [props.subtitle] - Subtitle text to display in the modal.
 * @param {String} props.initialNameValue - Initial value for the name input field.
 * @param {String} [props.initialDescriptionValue] - Initial value for the description text area.
 * @returns {React.ReactPortal} The EditVersionModal component.
 */
export function EditVersionModal({
  onClose = () => {},
  onSubmit = () => {},
  isOpen,
  nameLabel = 'Name',
  isNameFieldEditable = true,
  subtitle = undefined,
  initialNameValue,
  initialDescriptionValue = undefined
}) {
  const [name, setName] = useState(initialNameValue);
  const [description, setDescription] = useState(initialDescriptionValue);

  const trimmedName = name?.trim();
  const trimmedDescription = description?.trim();
  const canSave =
    (trimmedName !== initialNameValue && trimmedName !== '') ||
    (trimmedName !== '' && trimmedDescription !== initialDescriptionValue);

  const handleClose = () => {
    setName(initialNameValue);
    setDescription(initialDescriptionValue);
    onClose();
  };

  return createPortal(
    <Styled.EditVersionModal size="md" open={isOpen} onClose={handleClose} preventCloseOnClickOutside>
      <ModalHeader title="Edit version" />

      <ModalBody>
        <Stack gap={4}>
          {subtitle && <span>{subtitle}</span>}

          <TextInput
            id="version-name"
            type="text"
            labelText={nameLabel}
            maxCount={255}
            disabled={!isNameFieldEditable}
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-modal-primary-focus
          />

          <TextArea
            id="version-description"
            labelText="Description"
            placeholder="What changed from the previous version?"
            value={description}
            enableCounter
            counterMode="character"
            maxCount={255}
            onChange={(e) => setDescription(e.target.value)}
            autoComplete="off"
          />
        </Stack>
      </ModalBody>

      <ModalFooter>
        <Button kind="secondary" onClick={handleClose}>
          Cancel
        </Button>

        <Button kind="primary" disabled={!canSave} onClick={() => onSubmit(trimmedName, trimmedDescription)}>
          Save
        </Button>
      </ModalFooter>
    </Styled.EditVersionModal>,
    document.body
  );
}
